import { CloudTwoTone } from "@ant-design/icons";
import { CodeCreate } from "@openapi";
import { Card, App, Spin } from "antd";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import Link from "next/link";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CodeTableBase from "./CodeTableBase";
import useLocale from "@/hooks/useLocale";
import { useCodeListPages } from "@/hooks/useCodeListPages";
import type { CodeListFilter } from "@/hooks/useCodeListWindow";
import { cloudEditorUrl } from "@/lib/codeEditors";

interface CodeTableProps {}

const getEditorUrl = cloudEditorUrl;

const CodeTableCloud: React.FC<CodeTableProps> = () => {
    const { t } = useTranslation();
    const { notification } = App.useApp();
    const { isLoggedIn, user, loading } = useAuth();
    const locale = useLocale();
    const [filter, setFilter] = useState<CodeListFilter>({
        term: "",
        types: [],
        order: "descend",
    });

    const onLoadError = useCallback(
        (error: unknown) => {
            console.error("Error fetching cloud codes:", error);
            // Suppress UI noise on 401 — the global handler in AuthContext
            // will clear auth state and redirect to /login.
            const status =
                error &&
                typeof error === "object" &&
                "response" in error &&
                error.response instanceof Response
                    ? error.response.status
                    : undefined;
            if (status !== 401) {
                notification.error({
                    title: "Failed to load cloud codes",
                    description:
                        error instanceof Error
                            ? error.message
                            : "Unknown error",
                    placement: "bottomRight",
                });
            }
        },
        [notification],
    );

    // The user's codes come from the server a page at a time, filtered and
    // sorted there (blocks-code/studio#249).
    const pages = useCodeListPages(filter, !loading && isLoggedIn, onLoadError);
    const { remove } = pages;

    const handleSave = useCallback(
        async (newCode: CodeCreate) => {
            try {
                if (user) newCode.user_id = user.id; // Replace with actual user ID logic
                const createdCode = await api.codes.createCode({
                    codeCreate: newCode,
                });
                // Redirect to the editor page for the newly created code
                const editorUrl = getEditorUrl(
                    createdCode.type,
                    createdCode.id,
                );
                window.location.href = `/${locale}${editorUrl}`; // Redirect using window.location
            } catch (error) {
                console.error("Error saving code to cloud:", error);
                notification.error({
                    title: "Creation Failed",
                    description:
                        "An error occurred while creating the code. This code type may not be supported in cloud mode yet.",
                    placement: "bottomRight",
                });
            }
        },
        [notification, user, locale],
    );

    const handleDelete = useCallback(
        async (id: string) => {
            try {
                await api.codes.deleteCode({ codeId: id });
                remove(id);
                notification.success({
                    title: "Code Deleted",
                    description: "The code has been successfully deleted.",
                    placement: "bottomRight",
                });
            } catch (error) {
                console.error("Error deleting code:", error);
                notification.error({
                    title: "Deletion Failed",
                    description: "An error occurred while deleting the code.",
                    placement: "bottomRight",
                });
            }
        },
        [notification, remove],
    );

    if (loading) {
        return (
            <Card
                title={
                    <span>
                        <CloudTwoTone /> {t("codeTable.cloud")}
                    </span>
                }
                variant="outlined"
                style={{ height: "100%" }}
            >
                <div style={{ textAlign: "center", padding: "2rem" }}>
                    <Spin size="large" />
                    <p style={{ marginTop: "1rem" }}>
                        {t("codeTable.loading")}
                    </p>
                </div>
            </Card>
        );
    }

    if (!isLoggedIn) {
        return (
            <Card
                title={
                    <span>
                        <CloudTwoTone /> {t("codeTable.cloud")}
                    </span>
                }
                variant="outlined"
                style={{ height: "100%" }}
            >
                <div style={{ textAlign: "center" }}>
                    <Link href="/login">{t("codeTable.loginRequired")}</Link>
                </div>
            </Card>
        );
    }

    return (
        <CodeTableBase
            icon={<CloudTwoTone />}
            rows={pages.visible}
            total={pages.total}
            hasMore={pages.hasMore}
            onLoadMore={pages.showMore}
            filter={filter}
            onFilterChange={setFilter}
            loading={pages.loading}
            handleSave={handleSave}
            handleDelete={handleDelete}
            getEditorUrl={getEditorUrl}
            isLocal={false}
        />
    );
};

export default CodeTableCloud;
